import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import sendIcon from "url:shared-assets/icons/white/paper-airplane.svg";
import iconMic from "url:shared-assets/icons/microphone.svg";
import iconCheck from "url:shared-assets/icons/primary/check.svg";
import iconFaces from "url:shared-assets/icons/faces.svg";
import iconPlus from "url:shared-assets/icons/plus.svg";
import iconClose from "url:shared-assets/icons/close.svg";
import iconDocument from "url:shared-assets/icons/white/document.svg";
import { Modal, ModalHeader, ModalBody } from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";
import { getLanguage } from "../../../lang/helpers";
import { UploadFile } from "../interfaces";
import { uploadFileAtom } from "../../../jotai_state";
import {CONFIG} from "../../../constants";

interface ChatInputProps {
  sendToOtherPage: boolean;
  handleSendMessage: (text: string, aiStart: any, uploadFile: UploadFile[]) => void;
  onHeightChange?: (height: number) => void; // Added prop for height change callback
  startVoice: () => void;
  coachStyle: string;
  setCoachStyle: (text: string) => void;
  emmaThinking: boolean;
  placeholder?: string;
  hideVoice?: boolean;
}

enum ChatError {
  NONE,
  FILE_TOO_LARGE,
  BAD_FILE_FORMAT,
  AI_REQUEST_FAILED,
  TOO_MANY_FILES,
}

const ChatThreadInput: React.FC<ChatInputProps> = ({
  sendToOtherPage,
  handleSendMessage,
  onHeightChange,
  startVoice,
  coachStyle,
  setCoachStyle,
  emmaThinking,
  placeholder,
  hideVoice = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showSelectStyle, setShowSelectStyle] = useState<boolean>(false);
  const [input, setInput] = useState<string>("");
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language);
  const [fileRaw, setFileRaw] = useAtom(uploadFileAtom);
  const [chatError, setChatError] = useState<ChatError>(ChatError.NONE);
  const modes = [
    [
      "assistant",
      "Neutral",
      lang === "sv" ? "Få snabba svar och hantera vardagliga uppgifter" : "Get quick answers and handle everyday tasks",
    ],
    [
      "coaching",
      "Coaching",
      lang === "sv"
        ? "Få vägledning för självreflektion och utveckling"
        : "Get guidance for self-reflection and growth",
    ],
  ];
  if (CONFIG.SYSTEM !== 'production') {
    modes.push(
      [
        "chaining",
        "Testing",
        "Staging only - find out how to prompt background info",
      ],
    )
  }
  const isSmallScreen = () => window.innerWidth < 501;
  const isMobile = isSmallScreen();
  const [showButtons, setShowButtons] = useState<boolean>(() => !isSmallScreen());

  const sendMessage = () => {

    if (blockSend()) return;

    if (input.trim() === "" && (!fileRaw || fileRaw.length === 0)) return; // Prevent sending empty messages

    handleSendMessage(input, null, fileRaw || []);
    // If this component is used in other page than AiThreads, reset the input
    if (!sendToOtherPage) {
      setFileRaw([]);
      setInput(""); // Clear the input field
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        // Blur the textarea to trigger onBlur and allow shrinking
        if (isSmallScreen()) textareaRef.current.blur();
      }
    }
    // Shrink chat input on small screens after sending
    if (isSmallScreen()) setShowButtons(false);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const handleResize = () => {
      if (containerRef.current && onHeightChange) {
        const height = containerRef.current.offsetHeight;
        onHeightChange(height);
      }
    };

    handleResize(); // Initial height measurement

    const observer = new ResizeObserver(handleResize);
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, onHeightChange]);

  const selectCoachStyle = (mode: string) => {
    setCoachStyle(mode);
    setShowSelectStyle(false);
  };

  const showCoachStyle = () => {
    for (const mode of modes) {
      if (mode[0] === coachStyle) {
        return mode[1];
      }
    }
    return "Coaching";
  };

  const removeFile = (idx: number) => {
    const newFiles = [...fileRaw];
    newFiles.splice(idx, 1);
    setFileRaw(newFiles);
    const fileInput = document.getElementById("profileFile") as HTMLInputElement;
    if(fileInput){
      fileInput.value = "";
    }
  };

  const pasteFile = (evt:any) =>{
    // stop unwanted pasted text/path
    if(evt.clipboardData.files)
    if(evt.clipboardData.files.length > 0){
      evt.preventDefault(); 
      handleFilesSelected(evt.clipboardData.files)
    } else {
      return;
    }
  }

 const dropFile = (evt: any) => {
    evt.stopPropagation();
    evt.preventDefault();
    if (evt.dataTransfer.files.length > 0) {
     handleFilesSelected(evt.dataTransfer.files);
    } else {
      alert("Error: We could not find any dropped files.");
    }
  };
  // Helper to process a single file
  const processFile = (_file: File): Promise<UploadFile | null> => {
    return new Promise((resolve) => {
      if (!_file) return resolve(null);
      if (_file.size > 12000000) {
        setChatError(ChatError.FILE_TOO_LARGE);
        return resolve(null);
      }
      let filetype = "";
      if (_file.type === "application/pdf") filetype = "pdf";
      else if (_file.type.match("image.*")) filetype = "image";
      else if (
        _file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        _file.type === "application/vnd.ms-excel"
      )
        filetype = "excel";
      else if (
        _file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        _file.type === "application/msword"
      )
        filetype = "word";
      else {
        setChatError(ChatError.BAD_FILE_FORMAT);
        return resolve(null);
      }
      const filename = _file.name;
      if (filetype === "excel") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const uint8Array = new Uint8Array(arrayBuffer);
          const binaryString = Array.from(uint8Array)
            .map((byte) => String.fromCharCode(byte))
            .join("");
          const base64String = btoa(binaryString);
          resolve({
            filename,
            filetype,
            bytes: base64String,
            length: base64String.length,
          });
        };
        reader.readAsArrayBuffer(_file);
      } else if (filetype === "word") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const uint8Array = new Uint8Array(arrayBuffer);
          const binaryString = Array.from(uint8Array)
            .map((byte) => String.fromCharCode(byte))
            .join("");
          const base64String = btoa(binaryString);
          resolve({
            filename,
            filetype,
            bytes: base64String,
            length: base64String.length,
          });
        };
        reader.readAsArrayBuffer(_file);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            filename,
            filetype,
            bytes: e.target?.result as string,
            length: (e.target?.result as string).length,
          });
        };
        reader.readAsDataURL(_file);
      }
    });
  };
   // Multi-file handler for input
  const handleFilesSelected = (fileList: FileList) => {
    const currentFiles = fileRaw || [];
    const filesArray = Array.from(fileList);

    if (currentFiles.length + filesArray.length > 8) {
    setChatError(ChatError.TOO_MANY_FILES);
    return;
  }
    const availableSlots = 8 - currentFiles.length;
    const filesToAdd = filesArray.slice(0, availableSlots);

    Promise.all(filesToAdd.map(processFile)).then((newFiles) => {
      const validFiles = newFiles.filter((f) => f !== null) as UploadFile[];
      setFileRaw([...currentFiles, ...validFiles]);
    });
  };

  
  // const startVoiceError = () => {
  //   let errorMsg = lang === 'sv' ? 'Röstfunktionen är tillfälligt ur funktion på grund av serverproblem hos Microsoft Azure. Vi arbetar aktivt med att lösa problemet.' :
  //     'The voice function is temporarily unavailable due to server issues at Microsoft Azure. We are actively working to resolve the problem.'
  //   alert(errorMsg)
  // }

  const closeChatError = () => {
    setFileRaw([]);
    setChatError(ChatError.NONE);
  };

  const renderChatError = () => {
    let header = "Unknown error";
    if (chatError === ChatError.AI_REQUEST_FAILED) {
      header = lang === "sv" ? "AI-anropet misslyckades" : "AI request failed";
    } else if (chatError === ChatError.BAD_FILE_FORMAT) {
      header = lang === "sv" ? "Filformatet stöds inte" : "Unsupported file type";
    } else if (chatError === ChatError.FILE_TOO_LARGE) {
      header = lang === "sv" ? "Filstorleken är för stor" : "File size too large";
    } else if (chatError === ChatError.TOO_MANY_FILES) {
      header = lang === "sv" ? "För många filer" : "Too many files";
    }
    return (
      <Modal isOpen={true} onClose={() => closeChatError()}>
        <ModalHeader onClose={() => closeChatError()}>{header}</ModalHeader>
        <ModalBody>
          {chatError === ChatError.AI_REQUEST_FAILED && (
            <div className="text-left">
              {lang === "sv"
                ? "Ett fel uppstod när AI skulle svara på din fråga."
                : "An error occured when your request was handled."}
            </div>
          )}
          {chatError === ChatError.BAD_FILE_FORMAT && (
            <div className="text-left">
              {lang === "sv"
                ? "Detta filformat stöds inte. Ladda upp en PNG, JPEG, PDF, Word, eller Excel-fil."
                : "This file format isn't supported. Please upload a PNG, JPEG, PDF, Word, or Excel file."}
            </div>
          )}
          {chatError === ChatError.FILE_TOO_LARGE && (
            <div className="text-left">
              <p>
                {lang === "sv"
                  ? "Filen du laddar upp kan inte vara större än 12 MB."
                  : "The file you upload cannot be larger than 12 MB."}
              </p>
              <p className="mt-4">
                {lang === "sv" ? "Prova att ladda upp en mindre fil." : "Try uploading a smaller file."}
              </p>
            </div>
          )}
          {chatError === ChatError.TOO_MANY_FILES && (
            <div className="text-left">
              {lang === "sv"
                ? "Du kan bara ladda upp max 8 filer åt gången."
                : "You can only upload up to 8 files at a time."}
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  };

  const blockSend = () => {
    if (emmaThinking) {
      return true;
    }
    if (input.length < 1 && fileRaw === null) {
      return true;
    }
    return false;
  }

  return (
    <div
      className={`${
        showButtons ? "pb-2 pt-3" : "pb-1 pt-1"
      } rounded-md ring-1 ring-indigo-600 bg-indigo-900 focus-within:ring-2 focus-within:ring-primary-500 transition-all duration-150 ease-in-out relative`}
      ref={containerRef} // Reference to measure height
      onClick={() => {
        if (isSmallScreen()) setShowButtons(true);
      }}
      onDrop={(e) => {
        e.currentTarget.classList.remove("ring-primary-500");
        dropFile(e);
      }}
      onDragOver={(e) => {
        e.stopPropagation();
        e.preventDefault();
        e.currentTarget.classList.add("ring-primary-500");
      }}
      onDragLeave={(e) => {
        e.stopPropagation();
        e.preventDefault();
        e.currentTarget.classList.remove("ring-primary-500");
      }}
      onPaste={(e) => {
        e.currentTarget.classList.remove("ring-primary-500");
        pasteFile(e);
      }}
    >

      {fileRaw && fileRaw.length > 0 && (
        <div className="mb-2 px-4 flex flex-wrap gap-2">
          {fileRaw.map((file, idx) =>
            file.filetype === "image" ? (
              <div className="relative inline-block" key={idx}>
                <img src={file.bytes} alt="uploaded" className="z-uploaded-file rounded-[6px]" />
                <div
                  className="absolute top-1 right-1 rounded-full bg-indigo-900 h-6 w-6 p-1 cursor-pointer hover:bg-indigo-700"
                  onClick={() => removeFile(idx)}
                >
                  <img src={iconClose} className="h-4 w-4" alt="remove" />
                </div>
              </div>
            ) : file.filetype === "excel" ? (
              <div className="flex rounded-md bg-indigo-900 p-2 items-center relative" key={idx}>
                <div className="rounded-[4px] bg-indigo-700 p-2">
                  <img src={iconDocument} alt="excel" className="h-6 w-6" />
                </div>
                <div className="ml-2 text-left text-gray-400 z-title4">
                  <div className="truncate" style={{ width: 150 }}>
                    {file.filename}
                  </div>
                  <p>Excel</p>
                </div>
                <div
                  className="absolute top-1 right-1 rounded-full bg-indigo-900 h-6 w-6 p-1 cursor-pointer"
                  onClick={() => removeFile(idx)}
                >
                  <img src={iconClose} className="h-4 w-4" alt="remove" />
                </div>
              </div>
            ) :  file.filetype === "word" ? (
              <div className="flex rounded-md bg-indigo-900 p-2 items-center relative" key={idx}>
                <div className="rounded-[4px] bg-indigo-700 p-2">
                  <img src={iconDocument} alt="word" className="h-6 w-6" />
                </div>
                <div className="ml-2 text-left text-gray-400 z-title4">
                  <div className="truncate" style={{ width: 150 }}>
                    {file.filename}
                  </div>
                  <p>Word</p>
                </div>
                <div
                  className="absolute top-1 right-1 rounded-full bg-indigo-900 h-6 w-6 p-1 cursor-pointer"
                  onClick={() => removeFile(idx)}
                >
                  <img src={iconClose} className="h-4 w-4" alt="remove" />
                </div>
              </div>
            ) : (
              <div className="flex rounded-md bg-indigo-900 p-2 items-center relative" key={idx}>
                <div className="rounded-[4px] bg-indigo-700 p-2">
                  <img src={iconDocument} alt="pdf" className="h-6 w-6" />
                </div>
                <div className="ml-2 text-left text-gray-400 z-title4">
                  <div className="truncate" style={{ width: 150 }}>
                    {file.filename}
                  </div>
                  <p>PDF</p>
                </div>
                <div
                  className="absolute top-1 right-1 rounded-full bg-indigo-900 h-6 w-6 p-1 cursor-pointer"
                  onClick={() => removeFile(idx)}
                >
                  <img src={iconClose} className="h-4 w-4" alt="remove" />
                </div>
              </div>
            )
          )}
        </div>
      )}
      <div className="flex grow items-center">
        <textarea
          ref={textareaRef}
          placeholder={placeholder ? placeholder : t("chat-placeholder")}
          className={`flex-1  text-white rounded-md resize-none overflow-y-auto border-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none ${
            showButtons ? "px-4 py-2 max-h-40" : "px-3 py-1 pr-12 max-h-24"
          }`}
          style={{ whiteSpace: "normal" }}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (textareaRef.current) {
              textareaRef.current.style.height = "auto";
              textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
            }
          }}
          onFocus={() => setShowButtons(true)}
          //onBlur={() => {
          //  if (isSmallScreen()) setShowButtons(false);
          //}}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !isMobile) {
              e.preventDefault();
              sendMessage();
            }
          }}
          rows={1}
        />
      </div>

      <div className={`flex ${showButtons ? "mt-2" : "mt-1"} justify-between transition-all duration-150`}>
        <div className="flex ml-4">
          <div
            className={`${
              showButtons ? "flex" : "hidden"
            } relative align-middle cursor-pointer items-center justify-center bg-indigo-800 px-4 py-2 rounded-full hover:bg-indigo-700`}
            onClick={() => setShowSelectStyle(!showSelectStyle)}
          >
            {showSelectStyle && (
              <Modal isOpen={true} onClose={() => setShowSelectStyle(false)}>
                <ModalHeader onClose={() => setShowSelectStyle(false)}>
                  {lang === "sv" ? "Välj samtalsstil" : "Select a conversation style"}
                </ModalHeader>
                <ModalBody>
                  <div>
                    {modes.map((mode, index) => (
                      <div
                        onClick={() => selectCoachStyle(mode[0])}
                        key={"mode_" + index}
                        className={
                          "p-2 rounded hover:bg-indigo-800 text-left" +
                          (mode[0] === coachStyle ? " bg-indigo-800" : "")
                        }
                      >
                        <div className="flex justify-between items-center">
                          <div className="font-medium">{mode[1]}</div>
                          {mode[0] === coachStyle && <img src={iconCheck} alt="checked" />}
                        </div>
                        <div className="mt-1 text-sm text-gray-400">{mode[2]}</div>
                      </div>
                    ))}
                  </div>
                </ModalBody>
              </Modal>
            )}
            <img src={iconFaces} alt="icon" className="h-4 w-4" />
            <div className="hidden lg:block ml-2 text-sm text-gray-400">{showCoachStyle()}</div>
          </div>

          <div
            className={`${
              showButtons ? "flex" : "hidden"
            } ${fileRaw.length >= 8 ? "opacity-40 pointer-events-none" : " hover:bg-indigo-700"} ml-2 align-middle cursor-pointer items-center justify-center bg-indigo-800 px-4 py-2 rounded-full`}
          >
            <input
              id="profileFile"
              type="file"
              className="hidden"
              multiple
              onChange={(e: any) => handleFilesSelected(e.target.files)}
              disabled={fileRaw.length >= 8}
            />
            <label htmlFor="profileFile" className="flex items-center cursor-pointer" style={{ margin: 0 }}>
              <img src={iconPlus} alt="icon" className="h-4 w-4" />
              <span className="hidden lg:block ml-2 text-sm text-gray-400">
                {lang === "sv" ? "Ladda upp" : "Upload"}
              </span>
            </label>
          </div>

          {
            !hideVoice &&
              <div
                  className={`${
                    showButtons ? "flex" : "hidden"
                  } ml-2 align-middle cursor-pointer items-center justify-center bg-indigo-800 px-4 py-2 rounded-full hover:bg-indigo-700`}
                  onClick={() => startVoice()}
              >
                  <img src={iconMic} alt="icon" className="h-4 w-4"/>
                  <div className="hidden lg:block ml-2 text-sm text-gray-400 cursor-pointer">
                    {lang === "sv" ? "Röstläge" : "Voice mode"}
                  </div>
              </div>

          }

        </div>

        {showButtons ? (
          <CustomButton
            buttonType="transparentBase"
            size="lg"
            isRound
            leftIcon={<img src={sendIcon} alt="send" className="p-2"/>}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              sendMessage();
            }}
            disabled={blockSend()}
            className="mx-2"
          />
        ) : (
          <CustomButton
            buttonType="transparentBase"
            size="lg"
            isRound
            leftIcon={<img src={sendIcon} alt="send" className="p-2" />}
            onClick={sendMessage}
            disabled={blockSend()}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          />
        )}
      </div>
      {chatError !== ChatError.NONE && renderChatError()}
    </div>
  );
};

export default ChatThreadInput;
